import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, Plus, ChevronDown, ChevronRight, Lock, Download, ExternalLink,
  Users, Loader2, X, Copy as CopyIcon,
} from "lucide-react";
import { api, API as API_BASE } from "../../lib/api";

/**
 * Admin manager for trustee meeting attendance.
 *
 *  - Create a meeting (title, scheduled time, location, agenda).
 *  - Get a public sign-in URL to open on the shared device.
 *  - Watch attendee count live (refresh button).
 *  - Close the meeting (no more sign-ins) and download the PDF roster.
 */
export default function MeetingsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState({});
  const [draft, setDraft] = useState({ title: "", scheduled_at: "", location: "", agenda: "" });
  const [creating, setCreating] = useState(false);
  const [closing, setClosing] = useState(null);
  const [attendees, setAttendees] = useState({});

  const load = async () => {
    try {
      const r = await api.get("/admin/meetings");
      setItems(r.data.items || []);
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);

  const createMeeting = async (e) => {
    e.preventDefault();
    if (!draft.title.trim() || !draft.scheduled_at) return toast.error("Title and scheduled time are required.");
    setCreating(true);
    try {
      await api.post("/admin/meetings", draft);
      toast.success("Meeting created");
      setDraft({ title: "", scheduled_at: "", location: "", agenda: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setCreating(false);
  };

  const closeMeeting = async (id) => {
    if (!confirm("Close this meeting? No further sign-ins will be accepted.")) return;
    setClosing(id);
    try {
      await api.post(`/admin/meetings/${id}/close`);
      toast.success("Meeting closed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setClosing(null);
  };

  const refreshAttendees = async (id) => {
    try {
      const r = await api.get(`/admin/meetings/${id}/attendees`);
      setAttendees((a) => ({ ...a, [id]: r.data.items || [] }));
    } catch (e) { toast.error(e.message); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <div className="space-y-6" data-testid="meetings-manager">
      <div>
        <h3 className="font-display text-xl text-navy flex items-center gap-2">
          <Calendar size={18} className="text-saffron" /> Trustee meeting attendance
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Create a meeting, open the public sign-in URL on a shared device at the start of the
          meeting, and download a signed PDF attendance roster afterwards.
        </p>
      </div>

      <form onSubmit={createMeeting} className="bg-white border border-navy/10 rounded-sm p-5 space-y-3" data-testid="meeting-create-form">
        <h4 className="font-display text-base text-navy">New meeting</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="m-title" required />
          <Input label="Scheduled at" value={draft.scheduled_at} onChange={(v) => setDraft({ ...draft, scheduled_at: v })} testid="m-scheduled" type="datetime-local" required />
          <Input label="Location (optional)" value={draft.location} onChange={(v) => setDraft({ ...draft, location: v })} testid="m-location" />
        </div>
        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Agenda (optional)</span>
          <textarea
            rows={3}
            value={draft.agenda}
            onChange={(e) => setDraft({ ...draft, agenda: e.target.value })}
            data-testid="m-agenda"
            className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={creating}
          data-testid="m-create-btn"
          className="inline-flex items-center gap-1 px-4 py-2 bg-saffron text-navy rounded-sm text-xs font-medium hover:bg-saffron-light disabled:opacity-50"
        >
          {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create meeting
        </button>
      </form>

      <section>
        <h4 className="font-display text-base text-navy mb-3">Meetings ({items.length})</h4>
        <div className="space-y-2">
          {items.map((m) => {
            const isOpen = !!open[m.meeting_id];
            const signinUrl = `${window.location.origin}/meeting/${m.meeting_id}/signin`;
            return (
              <div key={m.meeting_id} className="bg-white border border-navy/10 rounded-sm" data-testid={`m-row-${m.meeting_id}`}>
                <button
                  onClick={() => setOpen((o) => ({ ...o, [m.meeting_id]: !isOpen }))}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-pearl/40"
                >
                  <div>
                    <div className="text-sm font-medium text-navy">{m.title}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {m.scheduled_at} · {m.location || "—"} · {m.attendee_count} signed in
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {m.status === "open" ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-green-50 text-green-700">Open</span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-slate-100 text-slate-500">Closed</span>
                    )}
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 border-t border-navy/10 pt-3 space-y-3" data-testid={`m-detail-${m.meeting_id}`}>
                    <div className="text-xs">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Public sign-in URL</div>
                      <div className="flex items-center gap-2">
                        <input
                          readOnly value={signinUrl}
                          data-testid={`m-url-${m.meeting_id}`}
                          className="flex-1 border border-navy/15 rounded-sm px-2 py-1.5 bg-pearl/40 text-xs font-mono"
                        />
                        <button onClick={() => copy(signinUrl)} className="px-2 py-1.5 text-xs text-saffron hover:underline inline-flex items-center gap-1">
                          <CopyIcon size={11} /> Copy
                        </button>
                        <a href={signinUrl} target="_blank" rel="noreferrer" className="px-2 py-1.5 text-xs text-saffron hover:underline inline-flex items-center gap-1">
                          <ExternalLink size={11} /> Open
                        </a>
                      </div>
                      <p className="mt-1 text-[10px] text-slate-400">Open this on a shared tablet / laptop at the meeting room. Every attendee taps to sign in.</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap pt-2">
                      <button
                        onClick={() => refreshAttendees(m.meeting_id)}
                        data-testid={`m-refresh-${m.meeting_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-navy/10 text-navy rounded-sm text-xs hover:bg-navy/20"
                      >
                        <Users size={12} /> Refresh attendees
                      </button>
                      <a
                        href={`${API_BASE}/admin/meetings/${m.meeting_id}/attendance.pdf`}
                        target="_blank" rel="noreferrer"
                        data-testid={`m-pdf-${m.meeting_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-saffron text-navy rounded-sm text-xs hover:bg-saffron-light"
                      >
                        <Download size={12} /> Roster PDF
                      </a>
                      {m.status === "open" && (
                        <button
                          onClick={() => closeMeeting(m.meeting_id)}
                          disabled={closing === m.meeting_id}
                          data-testid={`m-close-${m.meeting_id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-navy text-pearl rounded-sm text-xs hover:bg-navy-light disabled:opacity-50"
                        >
                          {closing === m.meeting_id ? <Loader2 size={12} className="animate-spin" /> : <Lock size={12} />} Close meeting
                        </button>
                      )}
                    </div>

                    {attendees[m.meeting_id] && (
                      <div className="bg-pearl/40 border border-navy/5 rounded-sm p-3">
                        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Attendees ({attendees[m.meeting_id].length})</p>
                        {attendees[m.meeting_id].length === 0 ? (
                          <p className="italic text-slate-500 text-xs">No one has signed in yet.</p>
                        ) : (
                          <ul className="text-xs space-y-1" data-testid={`m-attendees-${m.meeting_id}`}>
                            {attendees[m.meeting_id].map((a) => (
                              <li key={a.attendee_id} className="flex items-center justify-between gap-3">
                                <span className="text-navy">{a.full_name} <span className="text-slate-400">· {a.role || "—"}</span></span>
                                <span className="text-slate-400">{new Date(a.signed_at).toLocaleTimeString("en-GB")}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {items.length === 0 && <p className="italic text-slate-500">No meetings yet.</p>}
        </div>
      </section>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = "text", required }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      <input
        type={type} required={required} value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-navy/15 rounded-sm px-3 py-2 text-sm"
      />
    </label>
  );
}
